import { Alert, Dialog, Flex, Text } from '@servicetitan/anvil2';
import { ApplicationInstructions } from './ApplicationInstructions';
import { ApplicationDataStore, SaveFormDataToStore } from '../application';
import { ExternalApplicationFields } from './ExternalApplicationFields';
import { prepareExternalApplicationData } from './prepareExternalApplicationData';
import { mapExternalApplicationToBuyFinPrequal } from './mapExternalApplicationToBuyFinPrequal';
import { SubmitButtonGroup } from './SubmitButtonGroup';
import { Form, ImperativeForm } from '../common/Form';
import { ExternalApplicationHeader } from './ExternalApplicationHeader';
import { FinancedAmountStore, SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { SetupDataStore, useProviderConfig } from '@servicetitan/financing-workflow-shared';
import {
    DeliveryMethod,
    ExternalApplicationRequest,
    ExternalApplicationResponse,
    FinancingApi,
    LoanApplicationSubmitSource2,
    SubmittingUser,
} from '@servicetitan/financing-api-client';
import { PrequalsApi } from '@servicetitan/financing-api-client/src/prequals-api';
import { useRef, useEffect, useCallback } from 'react';
import { useDependencies } from '@servicetitan/react-ioc';
import { useMutation } from '@tanstack/react-query';
import { createSchema } from './schema';
import { z } from 'zod';
import { Log } from '@servicetitan/log-service';
import { openWebUrl } from '@servicetitan/financing-utils-shared';
import { ExternalApplicationStore } from './ExternalApplicationStore';

interface ExternalApplicationFormDialogProps {
    onSendViaEmail: () => void;
    onSendViaText?: () => void;
    onOpenApplication: (
        response?: ExternalApplicationResponse,
        isPrequalification?: boolean,
    ) => void;
    onCancel: () => void;
    allowSendViaEmail: boolean;
    showTechnicianInstructions: boolean;
    submittingUser?: SubmittingUser;
    source: LoanApplicationSubmitSource2;
    /** Opt-in per call site (prequal-choice flow only); never derived from setup data. */
    isPrequalification?: boolean;
    submitButtonText?: string;
    showLaunchIcon?: boolean;
}

export const ExternalApplicationFormDialog = ({
    onSendViaEmail,
    onSendViaText,
    onOpenApplication,
    onCancel,
    allowSendViaEmail,
    showTechnicianInstructions,
    submittingUser,
    source,
    isPrequalification = false,
    submitButtonText,
    showLaunchIcon = true,
}: ExternalApplicationFormDialogProps) => {
    const { setupData } = SetupDataStore.useStore();
    const { applicationData } = ApplicationDataStore.useStore();
    const { selectedPlan } = SelectedPlanStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const currentActionRef = useRef<'email' | 'application' | 'prequalification' | 'text' | null>(
        null,
    );
    const [apiClient, prequalsApi] = useDependencies(FinancingApi, PrequalsApi);
    const formRef = useRef<ImperativeForm>(null);

    const pendingDeliveryMethodRef = useRef<DeliveryMethod | null>(null);
    const hasAutoSubmittedRef = useRef<boolean>(false);
    const {
        showCreditScoreDisclosure = true,
        applicationAcknowledgementText,
        creditScoreDisclosureText,
        smsConsentText,
    } = useProviderConfig();
    const { setExternalApplicationData } = ExternalApplicationStore.useStore();
    const [log] = useDependencies(Log);

    let actualSchema = createSchema(isPrequalification, setupData?.provider);

    if (applicationAcknowledgementText) {
        actualSchema = actualSchema.merge(
            z.object({
                applicationAcknowledgement: z.boolean({
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    required_error: 'The applicant must attest to the disclaimer',
                }),
            }),
        );
    }

    if (smsConsentText) {
        actualSchema = actualSchema.merge(
            z.object({
                // z.literal(true) — not z.boolean() — so an unchecked (false/undefined) box blocks submit.
                smsConsent: z.literal(true, {
                    errorMap: () => ({ message: 'You must agree to the SMS terms to continue.' }),
                }),
            }),
        );
    }

    const externalApplicationParams: ExternalApplicationRequest = {
        providerName: setupData?.provider,
        tenantId: setupData?.tenantId,
        buId: setupData?.buId,
        amount: financedAmount,
        state: setupData?.state,
        jobId: setupData?.jobId,
        technicianId: setupData?.technicianId,
        source,
        submittingUser,
        applicant: {
            firstName: applicationData.externalApplication.firstName,
            lastName: applicationData.externalApplication.lastName,
            email: applicationData.externalApplication.email,
            phone: applicationData.externalApplication.primaryPhone,
            address: {
                street: applicationData.externalApplication.address?.street,
                address2: applicationData.externalApplication.address?.address2,
                city: applicationData.externalApplication.address?.city,
                state: applicationData.externalApplication.address?.state,
                zip: applicationData.externalApplication.address?.zip,
            },
        },
        lastFourSSN: applicationData.externalApplication.ssnLast4,
        applicantDeliveryMethod: DeliveryMethod.None,
        planNumber: selectedPlan?.planNumber,
        customerId: setupData?.customerId,
        isPrequalification,
        sendEmail: false,
        paymentFrequency: selectedPlan?.paymentFrequency ?? null,
        prequalId: setupData?.prequalificationId,
        // A prequalification is a soft pull for the current applicant; never tie a prescreen
        // pre-approval offer to it (keeps the API's pre-approval vs prequal split intact).
        preApprovalId: isPrequalification ? undefined : setupData?.preApprovalId,
    };

    const isBuyFin = setupData?.provider === 'BuyFin';
    const isAffirm = setupData?.provider === 'Affirm';

    const mutation = useMutation({
        mutationKey: ['externalApplication'],
        mutationFn: async ({ deliveryMethod }: { deliveryMethod: DeliveryMethod }) => {
            const finalApplicationData = prepareExternalApplicationData(externalApplicationParams);
            log.info({
                category: 'Financing.CustomerPortal2.ExternalApplication',
                message: 'Submitting external application',
                data: {
                    providerName: finalApplicationData.providerName ?? '',
                    tenantId: finalApplicationData.tenantId ?? '',
                    buId: finalApplicationData.buId ?? '',
                    amount: finalApplicationData.amount ?? '',
                    jobState: finalApplicationData.state ?? '',
                    jobId: finalApplicationData.jobId ?? '',
                    technicianId: finalApplicationData.technicianId ?? '',
                    loanSource: finalApplicationData.source,
                    customerId: finalApplicationData.customerId ?? '',
                    applicantLastName: finalApplicationData.applicant?.lastName ?? '',
                    applicantDeliveryMethod: deliveryMethod,
                    planNumber: finalApplicationData.planNumber ?? '',
                    isPrequalification: finalApplicationData.isPrequalification,
                },
            });

            // BuyFin uses PrequalsApi with general provider pattern
            if (isBuyFin) {
                const buyFinRequest = mapExternalApplicationToBuyFinPrequal(
                    {
                        ...finalApplicationData,
                        applicantDeliveryMethod: deliveryMethod,
                    },
                    submittingUser,
                );

                const response =
                    await prequalsApi.prequalificationsStartExternalPrequalificationWithoutLink(
                        {
                            providerName: 'General',
                            customerId: finalApplicationData.customerId,
                            jobId: finalApplicationData.jobId,
                        },
                        buyFinRequest,
                    );

                // Map BuyFin response to ExternalApplicationResponse format
                return {
                    data: {
                        id: null,
                        accessLink: response.data.redirectUrl,
                        redirectLink: response.data.redirectUrl,
                    } as ExternalApplicationResponse,
                };
            }

            // All other providers use FinancingApi
            return apiClient.loansSubmitExternalApplication({
                ...finalApplicationData,
                applicantDeliveryMethod: deliveryMethod,
            });
        },
        onSuccess: ({ data }: { data: ExternalApplicationResponse }, { deliveryMethod }) => {
            setExternalApplicationData({
                loanId: data?.id,
                accessLink: data?.accessLink,
                redirectLink: data?.redirectLink,
            });

            if (deliveryMethod === DeliveryMethod.Email) {
                onSendViaEmail();
                return;
            }

            if (deliveryMethod === DeliveryMethod.Text) {
                onSendViaText?.();
                return;
            }

            // Regular application handling
            onOpenApplication(data, isPrequalification);
            const linkToOpen = data?.accessLink ?? data?.redirectLink;
            if (!isPrequalification && linkToOpen) {
                openWebUrl(linkToOpen, setupData?.useInAppBrowser);
            }

            // Reset auto-submit flag on success to allow for potential future submissions
            hasAutoSubmittedRef.current = false;
        },
        onError: () => {
            currentActionRef.current = null;
            // Reset auto-submit flag on error to allow retry
            hasAutoSubmittedRef.current = false;
            // Error handling here
        },
    });

    const handleSendViaEmail = () => {
        pendingDeliveryMethodRef.current = DeliveryMethod.Email;
        currentActionRef.current = 'email';
        formRef.current?.submit();
    };

    const handleSendViaText = () => {
        pendingDeliveryMethodRef.current = DeliveryMethod.Text;
        currentActionRef.current = 'text';
        formRef.current?.submit();
    };

    // Get provider config early for use in callbacks
    const { enableExternalPrequalification } = useProviderConfig();

    const handleOpenApplication = useCallback(
        (isPrequalification: boolean) => {
            // For external prequalification, bypass form validation and call mutation directly
            if (enableExternalPrequalification) {
                pendingDeliveryMethodRef.current = DeliveryMethod.None;
                currentActionRef.current = 'prequalification';
                mutation.mutate({ deliveryMethod: DeliveryMethod.None });
                return;
            }

            // Regular flow for non-external prequalification or manual submissions
            pendingDeliveryMethodRef.current = DeliveryMethod.None;
            currentActionRef.current = isPrequalification ? 'prequalification' : 'application';

            if (!formRef.current) {
                return;
            }

            formRef.current.submit();
        },
        [enableExternalPrequalification, mutation, formRef],
    );

    const handleFormSubmit = () => {
        if (pendingDeliveryMethodRef.current !== null) {
            mutation.mutate({ deliveryMethod: pendingDeliveryMethodRef.current });
            pendingDeliveryMethodRef.current = null;
        }
    };

    // Auto-submit for external prequalification
    useEffect(() => {
        if (enableExternalPrequalification && !hasAutoSubmittedRef.current) {
            const timeoutId = setTimeout(() => {
                if (hasAutoSubmittedRef.current) {
                    return;
                }
                hasAutoSubmittedRef.current = true;
                handleOpenApplication(true);
            }, 100);

            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [enableExternalPrequalification, handleOpenApplication]);

    // Show loading screen instead of dialog for FinanceIt auto-submit
    if (enableExternalPrequalification) {
        return (
            <>
                <Dialog.Header />
                <Dialog.Content>
                    <Flex
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        gap="1"
                        style={{ padding: '2rem 1rem', paddingBottom: '4rem', width: '100%' }}
                    >
                        <Text size="large" style={{ fontWeight: 'bold', textAlign: 'center' }}>
                            Starting Pre-Qualification...
                        </Text>
                        <Text style={{ textAlign: 'center' }}>
                            Please wait while we prepare your Financeit application.
                        </Text>
                    </Flex>
                </Dialog.Content>
            </>
        );
    }

    return (
        <>
            <ExternalApplicationHeader />
            <Dialog.Content>
                <Flex gap="3" direction="column" style={{ width: '100%' }}>
                    {isAffirm && mutation.isError && (
                        <Alert status="danger" title="Unable to start Affirm application">
                            Something went wrong while starting your application. Please try again.
                        </Alert>
                    )}
                    <ApplicationInstructions technician={showTechnicianInstructions} />
                    <Form
                        onSubmit={handleFormSubmit}
                        formRef={formRef}
                        formData={applicationData.externalApplication}
                        schema={actualSchema}
                    >
                        <SaveFormDataToStore fieldPrefix="externalApplication" />
                        <ExternalApplicationFields isPrequalification={isPrequalification} />
                    </Form>
                </Flex>
            </Dialog.Content>
            <Dialog.Footer>
                <Flex
                    gap="3"
                    direction="column"
                    justifyContent="flex-end"
                    style={{ width: '100%' }}
                >
                    {showCreditScoreDisclosure && (
                        <Text size="small">
                            Submitting {creditScoreDisclosureText ?? 'an application'} will not
                            affect the customer's credit score.
                        </Text>
                    )}
                    <SubmitButtonGroup
                        onCancel={onCancel}
                        allowSendViaEmail={allowSendViaEmail}
                        onSendViaEmail={handleSendViaEmail}
                        onSendViaText={handleSendViaText}
                        onOpenApplication={() => handleOpenApplication(false)}
                        onOpenPrequalification={() => handleOpenApplication(true)}
                        isPrequalificationLoading={
                            mutation.isPending && currentActionRef.current === 'prequalification'
                        }
                        isApplicationLoading={
                            mutation.isPending && currentActionRef.current === 'application'
                        }
                        isSendViaEmailLoading={
                            mutation.isPending && currentActionRef.current === 'email'
                        }
                        isSendViaTextLoading={
                            mutation.isPending && currentActionRef.current === 'text'
                        }
                        submitButtonText={submitButtonText}
                        showLaunchIcon={showLaunchIcon}
                        isPrequalificationMode={isPrequalification}
                        showCancelButton={false}
                    />
                </Flex>
            </Dialog.Footer>
        </>
    );
};
